import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldListProps, FieldItem } from './types';
import {
  CalendarIcon,
  CheckBoxIcon,
  ChevronIcon,
  CommentsIcon,
  EditIcon,
  FormulaIcon,
  ImageIcon,
  RadioButtonIcon,
  DesignDetailsIcon,
  SourceIcon,
  StatusActiveIcon,
  TextboxIcon,
  LibraryIcon,
  DurationCounterIcon,
} from '../../../bombiix-icon';
import type { BombiixIconProps } from '../../../bombiix-icon/types';
import LibraryPanel from '../library-panel';
import { FIELD_LIBRARY_CONTROL_TYPES } from './cm-control-config.constants';

/**
 * FieldList component for displaying a list of fields in the system canvas.
 * Shows field titles with appropriate icons and provides edit functionality for each field.
 * Supports drag-and-drop reordering of fields.
 * @param fields - Array of field items to display
 * @param className - Optional CSS class name for styling
 * @param onChange - Callback function called when fields are reordered
 * @param onEdit - Callback function called when a field is selected for editing
 * @param selectedIndex - Optional index of the currently selected field for editing
 */
const FieldList: React.FC<FieldListProps> = ({
  fields,
  className = '',
  onChange,
  onEdit,
  selectedIndex,
  onDragStart,
  renderEditor,
  showLibraryButton = false,
  onLibraryPull,
}) => {
  const { t } = useTranslation('custom-module-system-canvas');
  const [isEditModeIndex, setIsEditModeIndex] = useState(-1);
  const [libraryOpenIndex, setLibraryOpenIndex] = useState(-1);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragIndexRef = useRef<number | null>(null);

  useEffect(() => {
    if (selectedIndex !== undefined) {
      setIsEditModeIndex(selectedIndex);
    } else {
      setIsEditModeIndex(-1);
    }
  }, [selectedIndex]);
  /**
   * Handles opening the edit modal for a field item or closing it
   * @param item - The field item to edit, or null to close
   */
  const openEdit = useCallback(
    (item: FieldItem | null) => {
      onEdit?.(item);
    },
    [onEdit],
  );

  /**
   * Returns the display title for a field item based on its type
   * @param item - The field item to get title for
   * @returns Display title string
   */
  const titleFor = useCallback(
    (item: FieldItem) => (item.config as { title?: string }).title || t(FIELD_TYPE_LABEL_KEYS[item.type]),
    [t],
  );

  /**
   * Returns the appropriate icon class for each field type
   */
  const iconFor: Record<string, React.FC<BombiixIconProps>> = useMemo(
    () => ({
      radio: RadioButtonIcon,
      text: TextboxIcon,
      dropdown: ChevronIcon,
      checkbox: CheckBoxIcon,
      date: CalendarIcon,
      comment: CommentsIcon,
      status: StatusActiveIcon,
      source: SourceIcon,
      image: ImageIcon,
      calculation: FormulaIcon,
      canvas: DesignDetailsIcon,
      time: DurationCounterIcon,
    }),
    [],
  );

  const handleDragStart = useCallback(
    (index: number, e: React.DragEvent) => {
      dragIndexRef.current = index;
      try {
        e.dataTransfer.setData('text/plain', String(index));
        onDragStart?.(index, e);
      } catch {}
    },
    [onDragStart],
  );

  const handleDragEnter = useCallback(
    (index: number) => {
      if (dragOverIndex !== index) {
        setDragOverIndex(index);
      }
    },
    [dragOverIndex],
  );

  const handleDragOver = useCallback(
    (index: number, e: React.DragEvent) => {
      e.preventDefault();
      if (dragOverIndex !== index) {
        setDragOverIndex(index);
      }
    },
    [dragOverIndex],
  );

  const handleDrop = useCallback(
    (index: number, e: React.DragEvent) => {
      e.preventDefault();
      const from = dragIndexRef.current;
      const to = index;

      if (from !== null && to !== null && from !== to && onChange) {
        const reorderedFields = [...fields];
        const [draggedItem] = reorderedFields.splice(from, 1);
        if (draggedItem) {
          reorderedFields.splice(to, 0, draggedItem);
          onChange(reorderedFields);
        }
      }

      dragIndexRef.current = null;
      setDragOverIndex(null);
    },
    [fields, onChange],
  );

  const handleDragEnd = useCallback(() => {
    dragIndexRef.current = null;
    setDragOverIndex(null);
  }, []);

  const toggleEdit = (item: FieldItem, index: number) => {
    if (isEditModeIndex === index) {
      setIsEditModeIndex(-1);
      openEdit(null);
    } else {
      setLibraryOpenIndex(-1);
      setIsEditModeIndex(index);
      openEdit(item);
    }
  };

  const toggleLibrary = (index: number) => {
    if (libraryOpenIndex === index) {
      setLibraryOpenIndex(-1);
      return;
    }
    if (isEditModeIndex !== -1) {
      setIsEditModeIndex(-1);
      openEdit(null);
    }
    setLibraryOpenIndex(index);
  };

  return (
    <div className={['cm-field-list', className].filter(Boolean).join(' ')}>
      {fields.map((item, index) => {
        const TypeIcon = iconFor[item.type];
        const isEditing = isEditModeIndex === index;
        const isLibraryOpen = libraryOpenIndex === index;
        return (
          <React.Fragment key={item.id}>
            <div
              draggable
              onDragStart={(e) => handleDragStart(index, e)}
              onDragEnter={() => handleDragEnter(index)}
              onDragOver={(e) => handleDragOver(index, e)}
              onDrop={(e) => handleDrop(index, e)}
              onDragEnd={handleDragEnd}
              className={['cm-field-entry', isEditing ? 'cm-field-entry--editing' : '', dragOverIndex === index ? 'cm-field-entry--over' : '']
                .filter(Boolean)
                .join(' ')}
            >
              <span className="cm-field-entry__type">{TypeIcon ? <TypeIcon size={FIELD_ROW_ICON_SIZE} /> : null}</span>
              <span className="cm-field-entry__divider" aria-hidden="true" />
              <span className="cm-field-entry__title">{titleFor(item)}</span>
              <span className="cm-field-entry__divider" aria-hidden="true" />
              <button
                type="button"
                className="cm-field-entry__edit"
                aria-label={t('COMMON.EDIT')}
                aria-pressed={isEditing}
                onClick={() => toggleEdit(item, index)}
              >
                <EditIcon size={FIELD_ROW_ICON_SIZE} />
              </button>
              {showLibraryButton && (
                <button
                  type="button"
                  className={['cm-field-entry__library', isLibraryOpen ? 'cm-field-entry__library--active' : ''].filter(Boolean).join(' ')}
                  aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.SAVE_TO_LIBRARY')}
                  aria-pressed={isLibraryOpen}
                  onClick={() => toggleLibrary(index)}
                >
                  <LibraryIcon size={FIELD_ROW_ICON_SIZE} />
                </button>
              )}
            </div>
            {isLibraryOpen ? <LibraryPanel controlType={FIELD_LIBRARY_CONTROL_TYPES[item.type]} content={item.config} onPull={(config) => onLibraryPull?.(index, config)} /> : null}
            {isEditing && renderEditor ? renderEditor(index) : null}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default FieldList;

const FIELD_ROW_ICON_SIZE = 20;

const FIELD_TYPE_LABEL_KEYS: Record<FieldItem['type'], string> = {
  dropdown: 'COMMON.FIELD_TYPES.DROPDOWN',
  text: 'COMMON.FIELD_TYPES.TEXT_BOX',
  radio: 'COMMON.FIELD_TYPES.RADIO_BUTTON',
  checkbox: 'COMMON.FIELD_TYPES.CHECKBOX',
  date: 'COMMON.FIELD_TYPES.DATE_PICKER',
  comment: 'COMMON.FIELD_TYPES.COMMENT',
  image: 'COMMON.FIELD_TYPES.IMAGE',
  status: 'COMMON.FIELD_TYPES.STATUS',
  source: 'COMMON.FIELD_TYPES.SOURCE',
  calculation: 'COMMON.FIELD_TYPES.CALCULATION',
  canvas: 'COMMON.FIELD_TYPES.CANVAS',
  time: 'COMMON.FIELD_TYPES.TIME_PICKER',
};
